import { type Dispatch, type SetStateAction, useCallback } from "react";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import type { TFunction } from "i18next";
import { type UseMutateFunction, useQueryClient } from "@tanstack/react-query";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";

export const useResourceFormChangeLanguage = ({
  t,
  copyLocale,
  clearStorage,
  setLang,
  resource,
  id,
}: {
  t: TFunction;
  copyLocale: UseMutateFunction<
    any,
    Error,
    {
      locale: string;
    }
  >;
  clearStorage: () => void;
  setLang: Dispatch<SetStateAction<string>>;
  resource: string;
  id: string | undefined;
}) => {
  const queries = useResourceQueries();

  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  return useCallback(
    async ({ lang, copy = false }: { lang: string; copy?: boolean }) => {
      if (copy) {
        copyLocale(
          {
            locale: lang,
          },
          {
            onSuccess: async () => {
              snackbar.success({
                flash: true,
                message: t("notifications.languageCopiedSuccess", {
                  ns: TranslationNamespaceEnum.FormComponent,
                }),
              });
              clearStorage();
              setLang(lang);
            },
            onError: () => {
              snackbar.error({
                flash: true,
                message: t("notifications.languageCopiedFailure", {
                  ns: TranslationNamespaceEnum.FormComponent,
                }),
              });
            },
          },
        );
      } else {
        clearStorage();
        setLang(lang);
        queryClient.invalidateQueries({
          queryKey: queries.detail({
            id: id!,
            resource,
            locale: lang,
          }),
        });
      }
    },
    [
      clearStorage,
      setLang,
      copyLocale,
      queryClient,
      id,
      resource,
      queries,
      t,
      snackbar,
    ],
  );
};
